/**
 * Contract Document Component
 * React component for rendering contract data as a styled document
 * Can be converted to PDF using html2canvas or used for print preview
 */

import React from 'react';
import { Box, Typography, Divider, Grid, Table, TableBody, TableCell, TableRow } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatCurrency as formatCurrencyBase } from '../../utils/formatters';
import { useTenant } from '../../contexts/TenantContext';

const ContractDocument = React.forwardRef(({ contract, id = 'contract-document' }, ref) => {
  const theme = useTheme();
  const { tenant } = useTenant();
  const dp = tenant?.roundingPrecision ?? 3;

  if (!contract) {
    return null;
  }

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return String(date);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount && amount !== 0) return 'N/A';
    return formatCurrencyBase(amount, currency, dp);
  };

  const SectionHeader = ({ children }) => (
    <Box
      sx={{
        backgroundColor: theme.palette.grey[100],
        padding: '8px 12px',
        marginBottom: '12px',
        borderLeft: `4px solid ${theme.palette.primary.main}`,
      }}
    >
      <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
        {children}
      </Typography>
    </Box>
  );

  const FieldRow = ({ label, value }) => (
    <TableRow>
      <TableCell
        sx={{
          fontWeight: 'bold',
          width: '35%',
          borderBottom: '1px solid #e0e0e0',
          padding: '8px 12px',
          color: theme.palette.grey[700],
        }}
      >
        {label}
      </TableCell>
      <TableCell
        sx={{
          borderBottom: '1px solid #e0e0e0',
          padding: '8px 12px',
        }}
      >
        {value || 'N/A'}
      </TableCell>
    </TableRow>
  );

  return (
    <Box
      id={id}
      ref={ref}
      sx={{
        backgroundColor: '#ffffff',
        padding: '30px',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        maxWidth: '210mm',
        margin: '0 auto',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: '#ffffff',
          padding: '20px',
          marginBottom: '20px',
          borderRadius: '4px 4px 0 0',
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          CAPITAL MANAGEMENT SYSTEM
        </Typography>
        <Typography variant="h6">Investment Contract</Typography>
      </Box>
      {/* Contract Reference Banner */}
      <Box
        sx={{
          backgroundColor: theme.palette.secondary.main,
          color: '#ffffff',
          padding: '12px 16px',
          marginBottom: '20px',
          borderRadius: '4px',
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Contract Reference: {contract.contractReference || 'N/A'}
        </Typography>
      </Box>
      {/* Contract Details */}
      <Box sx={{ marginBottom: '20px' }}>
        <SectionHeader>CONTRACT DETAILS</SectionHeader>
        <Table size="small">
          <TableBody>
            <FieldRow label="Contract Date" value={formatDate(contract.contractDate)} />
            <FieldRow label="Status" value={contract.status} />
            <FieldRow label="Product" value={contract.productName} />
            {contract.productDescription && (
              <FieldRow label="Description" value={contract.productDescription} />
            )}
          </TableBody>
        </Table>
      </Box>
      {/* Investor Details */}
      <Box sx={{ marginBottom: '20px' }}>
        <SectionHeader>INVESTOR DETAILS</SectionHeader>
        <Table size="small">
          <TableBody>
            <FieldRow label="Name" value={contract.investorName} />
            <FieldRow label="Email" value={contract.investorEmail} />
            <FieldRow label="Phone" value={contract.investorPhone} />
          </TableBody>
        </Table>
      </Box>
      {/* Financial Details */}
      <Box sx={{ marginBottom: '20px' }}>
        <SectionHeader>FINANCIAL DETAILS</SectionHeader>
        <Table size="small">
          <TableBody>
            <FieldRow
              label="Capital Amount"
              value={formatCurrency(contract.capitalAmount, contract.currencyCode)}
            />
            <FieldRow
              label="Interest Rate"
              value={`${contract.interestRate}% (${contract.rateType || 'Fixed'})`}
            />
            <FieldRow
              label="Currency"
              value={`${contract.currencyCode} - ${contract.currencyName || ''}`}
            />
            <FieldRow label="Deposit Date" value={formatDate(contract.depositDate)} />
            <FieldRow label="Maturity Date" value={formatDate(contract.maturityDate)} />
            <FieldRow
              label="Term"
              value={`${contract.term} ${contract.termUnit || 'Months'}`}
            />
          </TableBody>
        </Table>
      </Box>
      {/* Broker Details */}
      {contract.brokerName && (
        <Box sx={{ marginBottom: '20px' }}>
          <SectionHeader>BROKER DETAILS</SectionHeader>
          <Table size="small">
            <TableBody>
              <FieldRow label="Broker Name" value={contract.brokerName} />
              <FieldRow label="Broker Email" value={contract.brokerEmail} />
              <FieldRow label="Broker Fee" value={`${contract.brokerFee}%`} />
            </TableBody>
          </Table>
        </Box>
      )}
      {/* Collateral Details */}
      {contract.collateralType && (
        <Box sx={{ marginBottom: '20px' }}>
          <SectionHeader>COLLATERAL DETAILS</SectionHeader>
          <Table size="small">
            <TableBody>
              <FieldRow label="Type" value={contract.collateralType} />
              <FieldRow
                label="Value"
                value={formatCurrency(contract.collateralValue, contract.currencyCode)}
              />
              <FieldRow label="Description" value={contract.collateralDescription} />
              <FieldRow label="Provider" value={contract.collateralProviderName} />
            </TableBody>
          </Table>
        </Box>
      )}
      {/* Notes */}
      {contract.notes && (
        <Box sx={{ marginBottom: '20px' }}>
          <SectionHeader>NOTES</SectionHeader>
          <Box sx={{ padding: '12px', backgroundColor: '#fafafa', borderRadius: '4px' }}>
            <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
              {contract.notes}
            </Typography>
          </Box>
        </Box>
      )}
      {/* Footer */}
      <Divider sx={{ marginTop: '30px', marginBottom: '15px' }} />
      <Grid container justifyContent="space-between">
        <Grid>
          <Typography variant="caption" color="textSecondary">
            Generated on: {new Date().toLocaleString()}
          </Typography>
        </Grid>
        <Grid>
          <Typography variant="caption" color="textSecondary">
            Capital Management System
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
});

ContractDocument.displayName = 'ContractDocument';

export default ContractDocument;
